import type { GroupDto, PaginatedResponse } from "@ied/shared-types";
import {
	createContext,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
	useContext,
	useMemo,
	useState,
} from "react";
import { useGetGroups } from "@/hooks/queries/use-get-groups";
import { useGetMembers } from "@/hooks/queries/use-get-members";
import type { MemberDto } from "@/types/members";
import {
	type PaginationContextType,
	PaginationProvider,
	usePaginationContext,
} from "../pagination-context";

type MembersTableContext = PaginationContextType & {
	groups: GroupDto[] | undefined;
	groupsFetched: boolean | undefined;
	members: PaginatedResponse<MemberDto> | undefined;
	membersFetched: boolean | undefined;
	selectedGroup: string | undefined;
	setSelectedGroup: Dispatch<SetStateAction<string | undefined>>;
	nameToAbbreviationMap: Record<string, string>;
	abbreviationToNameMap: Record<string, string>;
};

// biome-ignore lint/style/noNonNullAssertion: set by provider on render
const MembersContext = createContext<MembersTableContext>(undefined!);

// A custom hook to consume the context easily
export const useMemberTableContext = () => useContext(MembersContext);

export function PaginatedMembersTableProvider({ children }: PropsWithChildren) {
	return (
		<PaginationProvider>
			<MembersTableProvider>{children}</MembersTableProvider>
		</PaginationProvider>
	);
}

function MembersTableProvider({ children }: PropsWithChildren) {
	// pagination setup
	const pagination = usePaginationContext();

	// groups
	const [selectedGroup, setSelectedGroup] = useState<string | undefined>("all");
	const { data, isFetched } = useGetGroups();

	// members
	const { data: members, isFetched: isMembersFetched } = useGetMembers({
		group: selectedGroup,
		page: pagination.pageIndex + 1, // PocketBase uses 1-based pages
		perPage: pagination.pageSize,
	});

	// derivations
	const { nameToAbbrevMap, abbrevToNameMap } = useMemo(() => {
		const d: {
			nameToAbbrevMap: Record<string, string>;
			abbrevToNameMap: Record<string, string>;
		} = data?.reduce(
			(acc, curr) => {
				acc.nameToAbbrevMap[curr.group_name] = curr.abbreviation;
				acc.abbrevToNameMap[curr.abbreviation] = curr.group_name;
				return acc;
			},
			{
				nameToAbbrevMap: {} as Record<string, string>,
				abbrevToNameMap: {} as Record<string, string>,
			},
		) ?? { nameToAbbrevMap: {}, abbrevToNameMap: {} };
		return d;
	}, [data]);

	return (
		<MembersContext.Provider
			value={{
				groups: data,
				groupsFetched: isFetched,
				members,
				membersFetched: isMembersFetched,
				selectedGroup,
				setSelectedGroup,
				nameToAbbreviationMap: nameToAbbrevMap,
				abbreviationToNameMap: abbrevToNameMap,
				...pagination,
			}}
		>
			{children}
		</MembersContext.Provider>
	);
}
